import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Shield, 
  ArrowRight, 
  CheckCircle2,
  Clock,
  Phone,
  AlertTriangle,
  Heart,
  Users,
  Stethoscope,
  Search,
  Sparkles,
  Star
} from 'lucide-react';
import logoUrgSalud from '@/assets/logo-urgsalud-transparent.png';
import heroBgDesktop from '@/assets/hero-bg-desktop.webp';
import heroBgMobile from '@/assets/hero-bg-mobile.webp';
import { useServiceAreas, type ServiceAreaDB } from '@/hooks/useServiceAreas';
import { AreaCard } from '@/components/areas/AreaCard';

export default function Index() {
  const navigate = useNavigate();
  const { areas: serviceAreas, loading } = useServiceAreas();

  // Separate health area (featured) from others
  const healthArea = serviceAreas.find(a => a.id === 'salud');
  const otherAreas = serviceAreas.filter(a => a.id !== 'salud');

  const howItWorks = [
    { step: '1', title: 'Elige tu área', description: 'Selecciona el tipo de servicio que necesitas' },
    { step: '2', title: 'Conectamos contigo', description: 'Un profesional cercano acepta tu solicitud' },
    { step: '3', title: 'Recibe atención', description: 'El profesional llega a tu domicilio o te atiende online' },
  ];

  const features = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Respuesta Rápida',
      description: 'Conectamos con profesionales disponibles cerca de ti.',
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Atención a Domicilio',
      description: 'Profesionales que llegan a tu hogar cuando más lo necesitas.',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Profesionales Verificados',
      description: 'Todos nuestros profesionales cuentan con verificación de documentos.',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Múltiples Áreas',
      description: 'Salud, educación, tecnología, construcción, belleza y más en un solo lugar.',
    },
  ];

  return (
    <div className="min-h-dvh bg-background">
      {/* ═══════════════════════════════════════════ */}
      {/* HERO SECTION */}
      {/* ═══════════════════════════════════════════ */}
      <header className="hero-section">
        <div className="hero-bg-desktop" style={{ backgroundImage: `url(${heroBgDesktop})` }} />
        <div className="hero-bg-mobile" style={{ backgroundImage: `url(${heroBgMobile})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11, 31, 42, 0.70) 0%, rgba(11, 31, 42, 0.50) 100%)' }} />

        <div className="relative z-10">
          {/* Navigation */}
          <nav className="container mx-auto px-4 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logoUrgSalud} alt="UrgSalud Chile" className="h-10 md:h-12 w-auto drop-shadow-lg" width={48} height={48} />
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <button className="btn-hero-secondary text-sm md:text-base" onClick={() => navigate('/auth')}>
                Iniciar Sesión
              </button>
              <button className="btn-hero-primary text-sm md:text-base" onClick={() => navigate('/auth')}>
                Registrarse
              </button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="container mx-auto px-4 py-8 lg:py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              {/* Location Badge */}
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-4 py-2" style={{ backgroundColor: 'rgba(25, 195, 179, 0.2)', border: '1px solid rgba(25, 195, 179, 0.4)' }}>
                  <MapPin className="w-4 h-4" style={{ color: '#19C3B3' }} />
                  <span className="text-sm" style={{ color: '#FFFFFF' }}>Región de la Araucanía, Chile</span>
                </div>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 px-2 md:px-0" style={{ color: '#FFFFFF', fontWeight: 700, lineHeight: 1.15 }}>
                PROFESIONALES A TU ALCANCE
                <br />
                <span style={{ color: '#19C3B3' }}>MÚLTIPLES ÁREAS DE SERVICIO</span>
              </h1>

              <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 md:mb-8 max-w-2xl mx-auto px-2 md:px-0" style={{ color: 'rgba(230, 244, 246, 0.9)', fontWeight: 400 }}>
                Conectamos personas con profesionales verificados en salud, educación, tecnología, construcción, belleza y más. Atención a domicilio, presencial u online.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  className="btn-hero-primary flex items-center justify-center gap-2 text-base md:text-lg px-8 py-4"
                  onClick={() => {
                    document.getElementById('areas-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <Search className="w-5 h-5" />
                  Explorar Servicios
                </button>

                <button
                  className="btn-hero-secondary flex items-center justify-center gap-2 text-base md:text-lg px-8 py-4"
                  onClick={() => navigate('/urgencia')}
                >
                  <AlertTriangle className="w-5 h-5" />
                  Urgencia Salud
                </button>

                <button
                  className="card-professional flex items-center justify-center gap-2 text-base md:text-lg px-8 py-4"
                  onClick={() => navigate('/auth?type=profesional')}
                >
                  <Stethoscope className="w-5 h-5" />
                  Soy Profesional
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: '#19C3B3' }} />
                  <span className="hero-text-secondary text-sm md:text-base">{serviceAreas.length}+ Áreas de Servicio</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" style={{ color: '#19C3B3' }} />
                  <span className="hero-text-secondary text-sm md:text-base">Profesionales Verificados</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" style={{ color: '#19C3B3' }} />
                  <span className="hero-text-secondary text-sm md:text-base">Respuesta Rápida</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>


      {/* ═══════════════════════════════════════════ */}
      {/* FEATURED: HEALTH AREA */}
      {/* ═══════════════════════════════════════════ */}
      {healthArea && (
        <section className="py-10 lg:py-16 bg-gradient-to-b from-primary/5 to-background" id="areas-section">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Star className="w-4 h-4" />
                Área Principal
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                Salud y Cuidado
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                Profesionales de salud verificados disponibles para urgencias y atención programada a domicilio
              </p>
            </motion.div>

            {/* Health featured card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <div
                className="relative overflow-hidden rounded-3xl border-2 border-primary/20 bg-card cursor-pointer group hover:border-primary/40 hover:shadow-[0_12px_40px_-12px_hsl(var(--primary)/0.3)] transition-all duration-300"
                onClick={() => navigate('/area/salud')}
              >
                {/* Gradient top bar */}
                <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-secondary" />

                <div className="p-6 md:p-8 lg:p-10">
                  <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                        <healthArea.icon className="w-10 h-10 md:w-12 md:h-12 text-primary" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                          {healthArea.name}
                        </h3>
                        <p className="text-muted-foreground mt-1 text-sm md:text-base">
                          {healthArea.description}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {['Médico General', 'Enfermería', 'Kinesiología', 'Cuidador'].map((service) => (
                          <div key={service} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                            <span className="text-foreground/80">{service}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button
                          className="btn-hero-primary flex items-center justify-center gap-2 px-6 py-3"
                          onClick={(e) => { e.stopPropagation(); navigate('/urgencia'); }}
                        >
                          <AlertTriangle className="w-4 h-4" />
                          Atención de Urgencia
                        </button>
                        <button
                          className="btn-hero-secondary flex items-center justify-center gap-2 px-6 py-3"
                          onClick={(e) => { e.stopPropagation(); navigate('/area/salud'); }}
                        >
                          Agendar Cita
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* OTHER SERVICE AREAS */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-10 lg:py-14 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-10"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
              Más Áreas de Servicio
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Conecta con profesionales en diferentes áreas — todas verificadas y a tu alcance
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
            }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {otherAreas.map((area) => (
              <motion.div
                key={area.id}
                variants={{
                  hidden: { opacity: 0, y: 24, scale: 0.96 },
                  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 300, damping: 24 } },
                }}
                className="h-full"
              >
                <AreaCard
                  area={{ ...area, services: [] }}
                  onClick={() => {
                    if (area.isActive) {
                      navigate(`/area/${area.id}`);
                    }
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* HOW IT WORKS */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-10 lg:py-14 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">¿Cómo funciona?</h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Solicita atención profesional en 3 simples pasos
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-4xl mx-auto">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="text-center"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary text-primary-foreground text-xl md:text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm md:text-base">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* FEATURES */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-10 lg:py-14 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">¿Por qué UrgSalud?</h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Una plataforma pensada para conectar a quienes necesitan atención con quienes pueden brindarla
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="card-medical p-5 md:p-6 h-full hover:border-primary/30 transition-all">
                  <div className="p-3 rounded-xl bg-primary-soft w-fit mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-all">
                    {feature.icon}
                  </div>
                  <h3 className="text-base md:text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm md:text-base">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* TWO PANELS: PATIENT / PROFESSIONAL */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-10 lg:py-14 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">¿Quién eres?</h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              UrgSalud tiene dos experiencias diseñadas para ti
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Patient Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="card-medical p-6 md:p-8 hover:border-primary/30 transition-all"
            >
              <div className="p-3 md:p-4 rounded-2xl bg-primary-soft w-fit mb-5">
                <Heart className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3">Necesito un Servicio</h3>
              <p className="text-muted-foreground text-sm md:text-base mb-5">
                Busca y conecta con profesionales verificados en múltiples áreas de servicio.
              </p>
              <ul className="space-y-2 mb-5">
                {['Explora áreas de servicio', 'Solicita atención a domicilio u online', 'Profesionales verificados', 'Historial de atenciones'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                className="w-full btn-hero-primary flex items-center justify-center gap-2"
                onClick={() => navigate('/auth?type=paciente')}
              >
                Registrarme como Usuario
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Professional Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="card-medical p-6 md:p-8 hover:border-secondary/30 transition-all"
            >
              <div className="p-3 md:p-4 rounded-2xl bg-secondary-soft w-fit mb-5">
                <Stethoscope className="w-8 h-8 md:w-10 md:h-10 text-secondary" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold mb-3">Soy Profesional</h3>
              <p className="text-muted-foreground text-sm md:text-base mb-5">
                Ofrece tus servicios profesionales y conecta con clientes en tu zona.
              </p>
              <ul className="space-y-2 mb-5">
                {['Recibe solicitudes de servicio', 'Gestiona tu disponibilidad', 'Perfil profesional verificado', 'Múltiples áreas de especialización'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                className="w-full card-professional flex items-center justify-center gap-2"
                onClick={() => navigate('/auth?type=profesional')}
              >
                Registrarme como Profesional
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* CTA */}
      {/* ═══════════════════════════════════════════ */}
      <section className="py-8 lg:py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-2xl p-6 md:p-8 lg:p-12 text-center max-w-4xl mx-auto"
            style={{ backgroundImage: `url(${heroBgDesktop})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          >
            <div className="absolute inset-0 rounded-2xl" style={{ backgroundColor: 'rgba(15, 42, 68, 0.85)' }} />
            <div className="relative z-10">
              <Phone className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-5" style={{ color: '#19C3B3' }} />
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold hero-text-primary mb-4">
                ¿Necesitas un profesional ahora?
              </h2>
              <p className="hero-text-secondary text-sm md:text-base mb-6 md:mb-8 max-w-xl mx-auto">
                Explora nuestras áreas de servicio o solicita atención de urgencia en salud.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  className="btn-hero-primary flex items-center justify-center gap-2 px-8 py-4"
                  onClick={() => navigate('/urgencia')}
                >
                  <AlertTriangle className="w-5 h-5" />
                  Urgencia Salud
                </button>
                <button
                  className="btn-hero-secondary flex items-center justify-center gap-2 px-8 py-4"
                  onClick={() => navigate('/auth?type=paciente')}
                >
                  Registrarme
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════ */}
      {/* FOOTER */}
      {/* ═══════════════════════════════════════════ */}
      <footer className="bg-sidebar py-10 md:py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5 md:gap-6">
            <div className="flex items-center gap-3">
              <img src={logoUrgSalud} alt="UrgSalud Chile" className="h-8 md:h-10 w-auto brightness-0 invert" width={40} height={40} />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm">
              <Link to="/terminos" className="text-sidebar-foreground/80 hover:text-sidebar-foreground transition-colors">
                Términos y Condiciones
              </Link>
              <Link to="/privacidad" className="text-sidebar-foreground/80 hover:text-sidebar-foreground transition-colors">
                Política de Privacidad
              </Link>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sidebar-foreground/80 text-sm mb-1">Región de La Araucanía, Chile</p>
              <p className="text-sidebar-foreground/60 text-xs md:text-sm">
                © {new Date().getFullYear()} UrgSalud. Plataforma de coordinación y contacto.
              </p>
            </div>
          </div>
          <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-sidebar-border text-center">
            <p className="text-sidebar-foreground/70 text-xs">
              Este servicio no reemplaza la atención de emergencia oficial. En caso de emergencia vital, llame al 131 (SAMU).
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
